import { apiGet, apiPatch } from './client';
import type {
  IdxCompositionHistory,
  IdxCompositionHistoryBatchRequest,
  IdxCompositionHistoryKey,
} from '../types/IdxCompositionHistory';

function withIdKey(
  rows: Omit<IdxCompositionHistory, 'idKey'>[],
): IdxCompositionHistory[] {
  return rows.map((r) => ({ ...r, idKey: `${r.idxId}|${r.assetId}|${r.dDate}` }));
}

function stripIdKey(
  rows: IdxCompositionHistory[],
): Omit<IdxCompositionHistory, 'idKey'>[] {
  return rows.map(({ idKey, ...rest }) => rest);
}

function parseIdKey(idKey: string): IdxCompositionHistoryKey {
  const [i, a, d] = idKey.split('|');
  return { idxId: Number(i), assetId: Number(a), dDate: d };
}

function buildQuery(fromDate?: string, toDate?: string): string {
  const params = new URLSearchParams();
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getIdxCompositionsHistory(
  fromDate?: string,
  toDate?: string,
): Promise<IdxCompositionHistory[]> {
  const rows = await apiGet<Omit<IdxCompositionHistory, 'idKey'>[]>(
    `/api/idx-compositions-history${buildQuery(fromDate, toDate)}`,
  );
  return withIdKey(rows);
}

export async function saveIdxCompositionsHistoryBatch(
  added: IdxCompositionHistory[],
  modified: IdxCompositionHistory[],
  deletedIdKeys: string[],
  fromDate?: string,
  toDate?: string,
): Promise<IdxCompositionHistory[]> {
  const body: IdxCompositionHistoryBatchRequest = {
    added: stripIdKey(added),
    modified: stripIdKey(modified),
    deleted: deletedIdKeys.map(parseIdKey),
    fromDate,
    toDate,
  };
  const fresh = await apiPatch<
    IdxCompositionHistoryBatchRequest,
    Omit<IdxCompositionHistory, 'idKey'>[]
  >('/api/idx-compositions-history/batch', body);
  return withIdKey(fresh);
}
